import { useRef, useState, useCallback, useEffect } from "react";

/**
 * Lightweight before/after image comparison slider.
 * Pure pointer events, no external deps.
 */
export default function BeforeAfterSlider({ before, after, height = "aspect-[4/3]", testId = "before-after" }) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (e.buttons !== 1 && e.type === "pointermove") return;
      updateFromClientX(e.clientX);
    };
    const el = wrapRef.current;
    if (!el) return;
    const start = (e) => {
      el.setPointerCapture?.(e.pointerId);
      updateFromClientX(e.clientX);
    };
    el.addEventListener("pointerdown", start);
    el.addEventListener("pointermove", onMove);
    return () => {
      el.removeEventListener("pointerdown", start);
      el.removeEventListener("pointermove", onMove);
    };
  }, [updateFromClientX]);

  return (
    <div ref={wrapRef} className={`ba-wrap w-full ${height}`} data-testid={testId}>
      <img src={before} alt="Before" loading="lazy" />
      <div className="ba-after" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src={after} alt="After" loading="lazy" />
      </div>
      <span className="ba-label" style={{ left: 12 }}>Before</span>
      <span className="ba-label" style={{ right: 12, left: "auto" }}>After</span>
      <div className="ba-handle" style={{ left: `${pos}%` }}>
        <div className="ba-knob" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0B1426" strokeWidth="2">
            <path d="M8 7l-5 5 5 5" />
            <path d="M16 7l5 5-5 5" />
          </svg>
        </div>
      </div>
    </div>
  );
}
